// Shared chat state: unread count, socket connection, the conversation open on
// screen, and an event bus the chat components listen to.
//
// bus events:
//   "message"  payload of chat.message.created ({conversation_id, message_id, subject, sender, preview, created_at})
//   "read"     {conversation_id, last_read_message_id, unread_count}
import Vue from "vue";
import "./chat.css";
import api from "./api";
import { POLL_UNREAD_MS, UNREAD_MIN_INTERVAL_MS, chatEnabledFor, reverb, storedUser } from "./config";
import { SUPPORTED_TYPES } from "./entityRoutes";

const state = Vue.observable({
  started: false,
  connected: false,
  unread: 0,
  activeConversationId: null,
  entityTypes: null, // [{key, label}] this user may tag
  pageContext: null, // {type, id}: the record open in a sidebar/modal (see pageContext.js)
});

const bus = new Vue();

let echo = null;
let pollTimer = null;
let userId = null;
let lastUnreadAt = 0;
let unreadRequest = null;
const baseTitle = document.title;

function setTitle() {
  document.title = document.hidden && state.unread > 0 ? `(${state.unread}) ${baseTitle}` : baseTitle;
}

/**
 * Fetch the unread count. Calls within UNREAD_MIN_INTERVAL_MS of the last fetch
 * (page changes, several callers at once) reuse it unless `force` is set, and
 * concurrent calls share one request, so navigating doesn't hit the API each time.
 */
function refreshUnread(force = false) {
  if (!state.started) return Promise.resolve();
  if (unreadRequest) return unreadRequest;
  if (!force && Date.now() - lastUnreadAt < UNREAD_MIN_INTERVAL_MS) return Promise.resolve();

  lastUnreadAt = Date.now();
  unreadRequest = api
    .unreadCount()
    .then((count) => {
      state.unread = count;
      setTitle();
    })
    .catch(() => {})
    .finally(() => {
      unreadRequest = null;
    });
  return unreadRequest;
}

function startPolling() {
  stopPolling();
  pollTimer = setInterval(() => {
    // Hidden tabs don't poll; they catch up when shown again (onVisibility).
    if (!state.connected && !document.hidden) refreshUnread();
  }, POLL_UNREAD_MS);
}

function stopPolling() {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
}

function onVisibility() {
  setTitle();
  if (!document.hidden && !state.connected) refreshUnread();
}

function connectEcho() {
  const pusher = echo.connector.pusher;
  pusher.connection.bind("state_change", ({ current }) => {
    state.connected = current === "connected";
  });

  echo
    .private(`App.User.${userId}`)
    .listen(".chat.message.created", (payload) => {
      const mine = payload.sender && Number(payload.sender.id) === userId;
      if (!mine) refreshUnread(true);
      bus.$emit("message", { mine, ...payload });
    })
    .listen(".chat.conversation.read", (payload) => {
      state.unread = payload.unread_count;
      setTitle();
      bus.$emit("read", payload);
    });
}

const chat = {
  state,
  bus,

  /** Call once the user is signed in (App.vue). Safe to call again. */
  start() {
    const user = storedUser();
    if (state.started || !chatEnabledFor(user)) return;

    state.started = true;
    userId = Number(user.id);
    refreshUnread(true);
    startPolling();
    document.addEventListener("visibilitychange", onVisibility);

    // Live updates only when a Reverb key is configured; laravel-echo and pusher-js
    // are then loaded in their own chunk, so pages without them stay light.
    if (!reverb.key) return;
    const startedFor = userId;
    import(/* webpackChunkName: "chat-realtime" */ "./echo")
      .then(({ createEcho }) => {
        if (!state.started || userId !== startedFor || echo) return; // logged out meanwhile
        echo = createEcho();
        connectEcho();
      })
      .catch(() => {}); // polling keeps working
  },

  /** Call on logout. */
  stop() {
    stopPolling();
    document.removeEventListener("visibilitychange", onVisibility);
    if (echo) {
      echo.disconnect();
      echo = null;
    }
    Object.assign(state, { started: false, connected: false, unread: 0, activeConversationId: null, entityTypes: null, pageContext: null });
    userId = null;
    lastUnreadAt = 0;
    document.title = baseTitle;
  },

  refreshUnread,

  setUnread(count) {
    state.unread = count;
    setTitle();
  },

  /**
   * Listen to one conversation's channel while it is open.
   * Returns a function that stops listening.
   */
  watchConversation(conversationId, { created, deleted }) {
    if (!echo) return () => {};
    const name = `chat.conversation.${conversationId}`;
    echo
      .private(name)
      .listen(".chat.message.created", (payload) => created?.(payload))
      .listen(".chat.message.deleted", (payload) => deleted?.(payload));
    return () => echo?.leave(name);
  },

  currentUserId() {
    return userId;
  },

  /** Record types this user may tag and this frontend can link to (cached). */
  loadEntityTypes() {
    if (state.entityTypes) return Promise.resolve(state.entityTypes);
    return api
      .entityTypes(SUPPORTED_TYPES)
      .then((types) => (state.entityTypes = types))
      .catch(() => []);
  },
};

export default chat;
